import React, { useEffect, useMemo, useState } from 'react';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import { ApiError } from '../utils/apiError';
import { sanitizeOAuthLoginErrorMessage } from '../utils/authLogin';
import { GOOGLE_STATE_KEY } from './LoginPage';
import type { LoginResponse } from '../services/api/types/common';

function readOAuthParams(search: string): { code: string; state: string } {
  const params = new URLSearchParams(search);
  return {
    code: (params.get('code') || '').trim(),
    state: (params.get('state') || '').trim(),
  };
}

/** Survives React Strict Mode remounts so the one-time Google code is not double-redeemed. */
const googleOAuthInflight = new Map<string, Promise<LoginResponse>>();

export function GoogleOAuthCallbackPage() {
  const { completeLoginFromResponse, isAuthenticated, loading } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const { code, state } = useMemo(() => readOAuthParams(location.search), [location.search]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading || isAuthenticated) return;
    if (!code || !state) {
      setError('Missing Google authorization response. Try signing in again.');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        let pending = googleOAuthInflight.get(state);
        if (!pending) {
          const expectedState = sessionStorage.getItem(GOOGLE_STATE_KEY) || '';
          if (!expectedState || expectedState !== state) {
            setError('Google sign-in state mismatch. Try again from the login page.');
            return;
          }
          sessionStorage.removeItem(GOOGLE_STATE_KEY);
          pending = apiService.completeGoogleOAuthLogin({ code, state }).then((res) => res.data);
          googleOAuthInflight.set(state, pending);
        }

        const data = await pending;
        await completeLoginFromResponse(data);
        googleOAuthInflight.delete(state);
        if (!cancelled) {
          history.replace('/');
        }
      } catch (err) {
        googleOAuthInflight.delete(state);
        if (!cancelled) {
          const detail =
            err instanceof ApiError ? err.message : err instanceof Error ? err.message : '';
          setError(sanitizeOAuthLoginErrorMessage(detail ? new Error(detail) : err, 'Google'));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, state, completeLoginFromResponse, history, isAuthenticated, loading]);

  if (!loading && isAuthenticated) {
    return <Redirect to="/" />;
  }

  return (
    <div className="min-h-screen royal-loading flex items-center justify-center p-6">
      <div className="royal-card p-8 max-w-md text-center border border-primary-500/20">
        {error ? (
          <>
            <p className="text-sm text-red-600" role="alert">{error}</p>
            <Link to="/login" className="text-primary-600 font-medium text-sm mt-4 inline-block">
              Back to sign in
            </Link>
          </>
        ) : (
          <>
            <div className="royal-loading-spinner mx-auto mb-4" aria-hidden />
            <p className="text-sm text-secondary-700">Completing Google sign-in…</p>
          </>
        )}
      </div>
    </div>
  );
}
